// src/components/ConnectionsView.tsx
import React, {useMemo, useState} from 'react';
import {Link, Waypoints, Wrench} from 'lucide-react';
import {useSimulationStore} from '../../store/useSimulationStore';
import {useInView} from '../../hooks/useInView';
import {ConnectionData, NodeData} from '../../store/useSimulationStore';
import KpiCard from '../common/KpiCard';
import SearchSortControls, {SortOption} from '../common/SearchSortControls';
import StatusBadge, {BadgePreset} from "../common/StatusBadge";
import Section from '../common/Section';
import Dot from '../common/StatusDot';

type TradeFilter = 'all' | 'trading' | 'idle';
type StatusFilter = 'all' | 'rich' | 'middle' | 'poor';
type SortKey = 'traffic' | 'trade' | 'maintenance' | 'id';

// ─── Extracted row component — each gets its own useInView ────────────────────
const ConnectionRow: React.FC<{
   conn: ConnectionData;
   nodesById: Record<number, NodeData>;
   onSelect: (id: number) => void;
   onSelectNode: (id: number | null) => void;
}> = ({conn, nodesById, onSelect, onSelectNode}) => {
   const [ref, inView] = useInView<HTMLDivElement>({rootMargin: '500px', once: false});
   
   const nodeA = nodesById[conn.node_a_id];
   const nodeB = nodesById[conn.node_b_id];
   const isTrading = (Number(conn.last_trade_volume) || 0) > 0;
   const traffic = Number(conn.traffic_volume) || 0;
   const maint = Number(conn.maintenance_cost) || 0;
   const tradeVol = Number(conn.last_trade_volume) || 0;
   const tradeVal = Number(conn.last_trade_value) || 0;
   
   return (
      <div
         ref={ref}
         onClick={() => onSelect(conn.id)}
         className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors hover:bg-gray-800 ${
            isTrading ? 'border-l-2 border-purple-600' : 'border-l-2 border-transparent'
         }`}
      >
         {inView ? (
            <>
               <span className="text-gray-600 text-xs font-mono w-8 shrink-0">#{conn.id}</span>
               
               <div className="flex items-center gap-1.5 min-w-0 flex-1">
                  <button
                     onClick={e => {
                        e.stopPropagation();
                        onSelectNode(nodeA?.id ?? null);
                     }}
                     className="text-white text-sm font-medium truncate hover:text-blue-300 transition-colors">
                     {nodeA?.name ?? `#${conn.node_a_id}`}
                  </button>
                  <StatusBadge preset={nodeA?.income_status as BadgePreset}>{nodeA?.income_status}</StatusBadge>
               </div>
               
               <span className={`text-xs shrink-0 font-mono ${isTrading ? 'text-purple-400' : 'text-gray-600'}`}>
                  {isTrading ? '⇄' : '↔'}
               </span>
               
               <div className="flex items-center gap-1.5 min-w-0 flex-1">
                  <button
                     onClick={e => {
                        e.stopPropagation();
                        onSelectNode(nodeB?.id ?? null);
                     }}
                     className="text-white text-sm font-medium truncate hover:text-blue-300 transition-colors">
                     {nodeB?.name ?? `#${conn.node_b_id}`}
                  </button>
                  <StatusBadge preset={nodeB?.income_status as BadgePreset}>{nodeB?.income_status}</StatusBadge>
               </div>
               
               <div className="flex items-center gap-3 shrink-0 text-right">
                  <div className="hidden sm:block">
                     <div className="text-xs text-gray-500">Traffic</div>
                     <div className="text-blue-300 text-sm font-mono">{traffic.toLocaleString()}</div>
                  </div>
                  <div className="hidden md:block">
                     <div className="text-xs text-gray-500">Maint.</div>
                     <div className="text-red-400 text-sm font-mono">−{maint.toFixed(0)}</div>
                  </div>
                  <div className="hidden md:block">
                     <div className="text-xs text-gray-500">Trade vol.</div>
                     {isTrading
                        ? <div className="text-purple-300 text-sm font-mono">{tradeVol.toFixed(0)}</div>
                        : <div className="text-sm">&nbsp;</div>}
                  </div>
                  <div className="hidden md:block">
                     <div className="text-xs text-gray-500">Trade val.</div>
                     {isTrading
                        ? <div className="text-green-400 text-sm font-mono">+{tradeVal.toFixed(0)}</div>
                        : <div className="text-sm">&nbsp;</div>}
                  </div>
               </div>
            </>
         ) : (
            <div className="flex items-center gap-3 px-4 py-3 w-full animate-pulse">
               <div className="w-8 h-4 bg-gray-800 rounded" />
               <div className="flex-1 shrink-0 grow h-[1.375rem] bg-gray-800 rounded-lg" />
               <div className="hidden sm:block w-48 h-9 bg-gray-800 rounded-lg" />
            </div>
         )}
      </div>
   );
};

// ─── Main view ────────────────────────────────────────────────────────────────
const ConnectionsView: React.FC = () => {
   const nodes = useSimulationStore(s => s.nodes);
   const connections = useSimulationStore(s => s.connections);
   const setSelectedConnectionId = useSimulationStore(s => s.setSelectedConnectionId);
   const setSelectedNodeId = useSimulationStore(s => s.setSelectedNodeId);
   
   const [search, setSearch] = useState('');
   const [tradeFilter, setTradeFilter] = useState<TradeFilter>('all');
   const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
   const [sortKey, setSortKey] = useState<SortKey>('traffic');
   const [sortAsc, setSortAsc] = useState(false);
   
   const nodesById = useMemo(
      () => Object.fromEntries(nodes.map(n => [n.id, n])),
      [nodes]
   );
   
   const stats = useMemo(() => {
      const totalTraffic = connections.reduce((s, c) => s + (Number(c.traffic_volume) || 0), 0);
      const totalMaintenance = connections.reduce((s, c) => s + (Number(c.maintenance_cost) || 0), 0);
      const activeTrades = connections.filter(c => (Number(c.last_trade_volume) || 0) > 0);
      const totalTradeVolume = activeTrades.reduce((s, c) => s + (Number(c.last_trade_volume) || 0), 0);
      const totalTradeValue = activeTrades.reduce((s, c) => s + (Number(c.last_trade_value) || 0), 0);
      return {totalTraffic, totalMaintenance, activeTrades, totalTradeVolume, totalTradeValue};
   }, [connections]);
   
   const filtered = useMemo(() => {
      let result = connections.filter(conn => {
         const nodeA = nodesById[conn.node_a_id];
         const nodeB = nodesById[conn.node_b_id];
         const isTrading = (Number(conn.last_trade_volume) || 0) > 0;
         
         if (tradeFilter === 'trading' && !isTrading) return false;
         if (tradeFilter === 'idle' && isTrading) return false;
         
         if (statusFilter !== 'all') {
            if (nodeA?.income_status !== statusFilter && nodeB?.income_status !== statusFilter) return false;
         }
         
         if (search.trim()) {
            const q = search.toLowerCase();
            const aName = (nodeA?.name ?? '').toLowerCase();
            const bName = (nodeB?.name ?? '').toLowerCase();
            if (!aName.includes(q) && !bName.includes(q)) return false;
         }
         
         return true;
      });
      
      return [...result].sort((a, b) => {
         let av = 0, bv = 0;
         switch (sortKey) {
            case 'traffic':
               av = Number(a.traffic_volume) || 0;
               bv = Number(b.traffic_volume) || 0;
               break;
            case 'trade':
               av = Number(a.last_trade_volume) || 0;
               bv = Number(b.last_trade_volume) || 0;
               break;
            case 'maintenance':
               av = Number(a.maintenance_cost) || 0;
               bv = Number(b.maintenance_cost) || 0;
               break;
            case 'id':
               av = a.id;
               bv = b.id;
               break;
         }
         return sortAsc ? av - bv : bv - av;
      });
   }, [connections, nodesById, tradeFilter, statusFilter, search, sortKey, sortAsc]);
   
   const toggleSort = (key: SortKey) => {
      if (sortKey === key) setSortAsc(p => !p);
      else {
         setSortKey(key);
         setSortAsc(false);
      }
   };
   
   const sortOptions: SortOption<SortKey>[] = [
      {key: 'traffic', label: 'Traffic'},
      {key: 'trade', label: 'Trade vol'},
      {key: 'maintenance', label: 'Maint'},
      {key: 'id', label: 'ID'},
   ];
   
   return (
      <div className="space-y-3">
         
         {/* ── Summary cards ── */}
         <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KpiCard icon={<Link className="w-4 h-4" />} label="Total Connections"
                     value={connections.length} color="text-white"
                     sub={`${stats.activeTrades.length} trading`} />
            <KpiCard icon={<Waypoints className="w-4 h-4" />} label="Total Traffic"
                     value={stats.totalTraffic.toLocaleString()} color="text-blue-400"
                     sub="units / tick" />
            <KpiCard icon={<Wrench className="w-4 h-4" />} label="Maintenance Cost"
                     value={stats.totalMaintenance.toLocaleString(undefined, {maximumFractionDigits: 0})} color="text-red-400"
                     sub="credits / tick" />
            <KpiCard icon={<Dot color="#c084fc" />} label="Trade Volume"
                     value={stats.totalTradeVolume.toLocaleString(undefined, {maximumFractionDigits: 0})} color="text-purple-400"
                     sub={`+${stats.totalTradeValue.toLocaleString(undefined, {maximumFractionDigits: 0})} credits`} />
         </div>
         
         {/* ── Controls ── */}
         <SearchSortControls
            search={search}
            onSearchChange={setSearch}
            searchPlaceholder="Search by connection name…"
            sortKey={sortKey}
            sortAsc={sortAsc}
            onSortChange={toggleSort}
            sortOptions={sortOptions}
            totalCount={connections.length}
            filteredCount={filtered.length}
         >
            <div className="flex gap-1.5 flex-wrap">
               {(['all', 'trading', 'idle'] as TradeFilter[]).map(f => (
                  <StatusBadge
                     key={f}
                     onClick={() => setTradeFilter(f)}
                     active={tradeFilter === f}
                     preset={f === 'trading' ? 'trading' : f === 'idle' ? 'generic' : undefined}
                     className={f === 'all' && tradeFilter === 'all'
                        ? 'bg-blue-900/50 border-blue-700 text-blue-300'
                        : undefined}
                  >
                     {f === 'trading'
                        ? <><Dot color="#c084fc" className="mr-1" /> Trading</>
                        : f === 'idle' ? <><Dot color="#6b7280" className="mr-1" /> Idle</>
                        : 'All'}
                  </StatusBadge>
               ))}
            </div>
            
            <div className="flex gap-1.5 flex-wrap">
               {(['all', 'rich', 'middle', 'poor'] as StatusFilter[]).map(f => (
                  <StatusBadge
                     key={f}
                     onClick={() => setStatusFilter(f)}
                     active={statusFilter === f}
                     preset={f === 'all' ? undefined : f}
                     className={f === 'all' && statusFilter === 'all'
                        ? 'bg-blue-900/50 border-blue-700 text-blue-300'
                        : undefined}
                  >
                     {f === 'all' ? 'Any status' : f}
                  </StatusBadge>
               ))}
            </div>
         </SearchSortControls>
         
         {/* ── Connection list ── */}
         <Section noPadding>
            {filtered.length === 0 ? (
               <div className="py-16 text-center text-gray-600 text-sm">No connections match your filters.</div>
            ) : (
               <div className="divide-y divide-gray-800">
                  {filtered.map(conn => (
                     <ConnectionRow
                        key={conn.id}
                        conn={conn}
                        nodesById={nodesById}
                        onSelect={setSelectedConnectionId}
                        onSelectNode={setSelectedNodeId}
                     />
                  ))}
               </div>
            )}
         </Section>
         
         <p className="text-xs text-gray-600 px-1">
            Traffic scales with population × distance. Opening a connection costs resources from both nodes;
            pruning also has a cost and triggers a cooldown period.
         </p>
      
      </div>
   );
};

export default ConnectionsView;
